/**
 * MiniGalleryPreview
 *
 * Compact inline strip showing the last 8 generated images (80×80px thumbnails).
 * Designed to sit at the bottom of ImagePreviewPanel, not as a fixed overlay.
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { Images } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSimpleGallery } from '@/hooks/useGalleryV2';
import { galleryImagesToLegacy } from '@/lib/api/gallery-v2';
import { cn } from '@/lib/utils';

interface MiniGalleryPreviewProps {
  onImageClick?: (imageId: string) => void;
}

export function MiniGalleryPreview({ onImageClick }: MiniGalleryPreviewProps) {
  const { t } = useTranslation();

  const { images: galleryImages, isLoading } = useSimpleGallery({
    page: 1,
    perPage: 8,
  });

  const images = galleryImagesToLegacy(galleryImages);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-3">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (images.length === 0) {
    return null;
  }

  return (
    <div
      id="mini-gallery-preview"
      className="border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 px-4 py-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <Images className="h-3.5 w-3.5 text-neutral-400" />
        <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
          {t('generation.recentGenerations', 'Letzte Generierungen')}
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto">
        {images.map((image) => (
          <button
            key={image.id}
            onClick={() => onImageClick?.(image.id)}
            className={cn(
              'flex-shrink-0 w-[80px] h-[80px] rounded-md overflow-hidden',
              'bg-neutral-100 dark:bg-neutral-800',
              'transition-all hover:ring-2 hover:ring-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500',
              'cursor-pointer'
            )}
            aria-label={`View image ${image.id}`}
          >
            <img
              src={image.image_url}
              alt={`Generated image ${image.id}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>
    </div>
  );
}
